/**
 * NiV Dashboard RTK Query Service
 *
 * This service provides RTK Query hooks for the NiV (SaaS Admin) dashboard.
 * Uses nivFirebase.js for all Firebase operations.
 */

import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  // NEW: Source-of-truth fetching
  getNivClinicsFromSource,
  getNivClinicFullDetail,
  getNivClinicActivitySeries,
  updateClinicFieldDirectly,
  ensureCrmRecord,
  // Clinic Management (CRM)
  createNivClinicManagement,
  getNivClinicManagement,
  getAllNivClinics,
  updateNivClinicManagement,
  updateNivClinicStatus,
  addNivCallLog,
  addNivPaymentRecord,
  updateClinicSmsLimit,
  // Tasks
  createNivTask,
  getNivTasks,
  getNivTask,
  updateNivTask,
  deleteNivTask,
  getNivTasksConfig,
  updateNivTasksConfig,
  bulkUpdateNivTasks,
  // Stats & Alerts
  getNivDashboardStats,
  getNivAlerts,
  getClinicUsageStats,
  getAllClinicsBasic,
  // Onboarding
  getOnboardingRequests,
  markOnboardingCompleted,
  getChurnResolutions,
  resolveChurnAlert,
  // Staff Therapist Additions
  getTherapistAdditions,
  resolveTherapistAddition,
  // Call Requests
  createCallRequest,
  createPublicLead,
  getCallRequests,
  updateCallRequestStatus,
  deleteCallRequest,
  addCallRequestNote,
  // Plans (Legacy - for backward compatibility)
  getNivPlans,
  createNivPlan,
  updateNivPlan,
  deleteNivPlan,
  seedDefaultPlans,
  // System Plans (New - hard-coded)
  SYSTEM_PLANS,
  getSystemPlansArray,
  // Sync & Revenue
  syncAllClinicsToManagement,
  updateClinicPlan,
  updateClinicPlanDirectly,
  updateClinicCustomPayment,
  setClinicPlanEndDate,
  getTotalMonthlyRevenue,
  // System Notifications
  getSystemNotifications,
  deleteSystemNotification,
  // Food promotion queue (nutrition clinics)
  getFoodPromotionRequests,
  approveFoodPromotionRequest,
  rejectFoodPromotionRequest,
  // System Messages (broadcasts)
  getNivSystemMessages,
  createNivSystemMessage,
  deleteNivSystemMessage,
  // Registration Whitelist
  addPhoneToWhitelist,
  removePhoneFromWhitelist,
  getRegistrationWhitelist,
  // Clinic CRM (manual admin layer)
  getNivClinicsOverview,
  updateNivClinicCrm,
  addNivCrmPayment,
  addNivCrmPaymentAndExtend,
  deleteNivCrmPayment,
  addNivCrmNote,
  // Clinic tags registry + contact tracking + activation (clinics tab)
  getNivClinicTagsRegistry,
  saveNivClinicTag,
  deleteNivClinicTag,
  setNivClinicTagIds,
  addNivCrmContact,
  deleteNivCrmContact,
  dismissNivContactNeed,
  getClinicsActivationCounts,
  // Prospects (unified CRM pipeline)
  getNivProspectsBoard,
  createProspect,
  updateProspectDetails,
  dismissStaleApprovedProspect,
  updateProspectStage,
  addProspectNote,
  approveProspectRegistration,
  revokeProspectRegistration,
  deleteProspect,
  linkProspectToClinic,
  unlinkProspectFromClinic,
  updateProspectFreeNotes,
  updateNivCrmFreeNotes,
  updateProspectNextAction,
  updateNivCrmNextAction,
  setStandingOrderMonthStatus,
  // Monitoring alerts (written by the dailyAdminMonitoring cron)
  getMonitoringAlerts,
  snoozeMonitoringAlert,
  snoozeMonitoringAlertsBulk,
  getLennyReviews,
  setLennyReviewStatus,
  getClinicLennyHistory,
  // Telemetry & scores
  getTelemetryOverview,
  getSystemDailySeries,
  getClinicUsageList,
  getClinicTelemetryDetail,
  getClinicFeatureTips,
  setClinicFeatureTipVisibility,
  // Error logs
  getErrorLogs,
  updateErrorLogStatus,
  deleteErrorLog,
  deleteAllErrorLogs,
} from "../../services/nivFirebase";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase";
import { serializeForRedux } from "../../utils/dateUtils";

/** Redux-shape mirror of nivFirebase's buildNextActionFields (for optimistic patches). */
const buildOptimisticNextAction = (text, at) => {
  const cleanText = (text || "").trim() || null;
  const atIso = at ? new Date(at).toISOString() : null;
  if (!cleanText && !atIso) return null;
  return {
    text: cleanText,
    at: atIso,
    pending: !!atIso,
    by: null,
    updatedAt: new Date().toISOString(),
    notifiedAt: null,
  };
};

export const nivApi = createApi({
  reducerPath: "nivApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: [
    "NivWhatsApp",
    "NivMessaging",
    "NivClinic",
    "NivTask",
    "NivTasksConfig",
    "NivStats",
    "NivAlerts",
    "NivOnboarding",
    "NivChurnResolution",
    "NivTherapistAddition",
    "NivPlan",
    "NivCallRequest",
    "NivSystemNotifications",
    "NivSystemMessages",
    "RegistrationWhitelist",
    "NivProspect",
    "NivClinicsOverview",
    "NivClinicTags",
    "NivClinicsActivation",
    "NivTelemetry",
    "NivUsage",
    "NivFeatureTips",
    "NivErrorLogs",
    // Cron-written niv_alerts (distinct from the legacy computed "NivAlerts")
    "NivMonitoringAlerts",
    // Lenny (AI support) review queue + per-clinic chat history
    "NivLennyReviews",
    // Clinic foods waiting to be promoted into the global catalog
    "NivFoodRequests",
    "NivLennyHistory",
    // Grow payments control plane (nivGrowAdmin callable)
    "NivGrow",
  ],
  endpoints: (builder) => ({
    // ============================================
    // DASHBOARD STATS & ALERTS
    // ============================================

    // Get dashboard statistics
    getNivDashboardStats: builder.query({
      async queryFn() {
        try {
          const stats = await getNivDashboardStats();
          return { data: stats };
        } catch (error) {
          console.error("[CK-9900] Error in getNivDashboardStats query:", {
            errorCode: "CK-9900",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: ["NivStats"],
    }),

    // Get alerts
    getNivAlerts: builder.query({
      async queryFn() {
        try {
          const alerts = await getNivAlerts();
          return { data: alerts };
        } catch (error) {
          console.error("[CK-9901] Error in getNivAlerts query:", {
            errorCode: "CK-9901",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: ["NivAlerts"],
    }),

    // ============================================
    // NEW: SOURCE-OF-TRUTH CLINIC FETCHING
    // ============================================

    // Get all clinics from source (clinics collection as primary)
    getNivClinicsFromSource: builder.query({
      async queryFn({ search, plan, crmStatus } = {}) {
        try {
          const clinics = await getNivClinicsFromSource({ search, plan, crmStatus });
          return { data: clinics };
        } catch (error) {
          console.error("[CK-9950] Error in getNivClinicsFromSource query:", {
            errorCode: "CK-9950",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ clinicId }) => ({ type: "NivClinic", id: clinicId })),
              { type: "NivClinic", id: "LIST" },
            ]
          : [{ type: "NivClinic", id: "LIST" }],
    }),

    // Get full clinic detail (combined clinic + CRM + stats)
    getNivClinicFullDetail: builder.query({
      async queryFn({ clinicId }) {
        try {
          const detail = await getNivClinicFullDetail(clinicId);
          return { data: detail };
        } catch (error) {
          console.error("[CK-9951] Error in getNivClinicFullDetail query:", {
            errorCode: "CK-9951",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { clinicId }) => [
        { type: "NivClinic", id: clinicId },
      ],
    }),

    // Per-clinic activity series (30d daily counts, last patient, money by month)
    getNivClinicActivitySeries: builder.query({
      async queryFn({ clinicId }) {
        try {
          const data = await getNivClinicActivitySeries(clinicId);
          return { data };
        } catch (error) {
          console.error("[CK-7996] Error in getNivClinicActivitySeries query:", {
            errorCode: "CK-7996",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { clinicId }) => [
        { type: "NivClinic", id: `${clinicId}-activity` },
      ],
    }),

    // Update a field directly on the clinics collection
    updateClinicField: builder.mutation({
      async queryFn({ clinicId, fieldUpdates, skipTimestamp = false }) {
        try {
          const result = await updateClinicFieldDirectly(clinicId, fieldUpdates, { skipTimestamp });
          return { data: result };
        } catch (error) {
          console.error("[CK-9952] Error in updateClinicField mutation:", {
            errorCode: "CK-9952",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { clinicId }) => [
        { type: "NivClinic", id: clinicId },
        { type: "NivClinic", id: "LIST" },
        "NivClinicsOverview",
      ],
    }),

    // Ensure CRM record exists for a clinic
    ensureCrmRecord: builder.mutation({
      async queryFn({ clinicId }) {
        try {
          const managementId = await ensureCrmRecord(clinicId);
          return { data: { clinicId, managementId } };
        } catch (error) {
          console.error("[CK-9953] Error in ensureCrmRecord mutation:", {
            errorCode: "CK-9953",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { clinicId }) => [
        { type: "NivClinic", id: clinicId },
      ],
    }),

    // ============================================
    // LEGACY CLINIC MANAGEMENT (kept for compatibility)
    // ============================================

    // Get all NiV clinic management records
    getNivClinics: builder.query({
      async queryFn({ search, status } = {}) {
        try {
          const clinics = await getAllNivClinics({ search, status });
          return { data: clinics };
        } catch (error) {
          console.error("[CK-9902] Error in getNivClinics query:", {
            errorCode: "CK-9902",
            errorMessage: error.message,
            search,
            status,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "NivClinic", id })),
              { type: "NivClinic", id: "LIST" },
            ]
          : [{ type: "NivClinic", id: "LIST" }],
    }),

    // Get single clinic management record
    getNivClinicDetail: builder.query({
      async queryFn({ managementId }) {
        try {
          const clinic = await getNivClinicManagement(managementId);
          return { data: clinic };
        } catch (error) {
          console.error("[CK-9903] Error in getNivClinicDetail query:", {
            errorCode: "CK-9903",
            errorMessage: error.message,
            managementId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { managementId }) => [
        { type: "NivClinic", id: managementId },
      ],
    }),

    // Get clinic usage stats (from actual clinic collection)
    getNivClinicUsageStats: builder.query({
      async queryFn({ clinicId }) {
        try {
          const stats = await getClinicUsageStats(clinicId);
          return { data: stats };
        } catch (error) {
          console.error("[CK-9920] Error in getNivClinicUsageStats query:", {
            errorCode: "CK-9920",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { clinicId }) => [
        { type: "NivClinic", id: `usage_${clinicId}` },
      ],
    }),

    // Get all clinics basic info (for creating management records)
    getAllClinicsBasic: builder.query({
      async queryFn() {
        try {
          const clinics = await getAllClinicsBasic();
          return { data: clinics };
        } catch (error) {
          console.error("[CK-9921] Error in getAllClinicsBasic query:", {
            errorCode: "CK-9921",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: "NivClinic", id: "ALL_CLINICS" }],
    }),

    // Create clinic management record
    createNivClinicManagement: builder.mutation({
      async queryFn({ data }) {
        try {
          const clinic = await createNivClinicManagement(data);
          return { data: clinic };
        } catch (error) {
          console.error(
            "[CK-9904] Error in createNivClinicManagement mutation:",
            {
              errorCode: "CK-9904",
              errorMessage: error.message,
              data: { clinicRef: data?.clinicRef },
              timestamp: new Date().toISOString(),
            }
          );
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [
        { type: "NivClinic", id: "LIST" },
        "NivStats",
        "NivAlerts",
      ],
    }),

    // Update clinic management record
    updateNivClinicManagement: builder.mutation({
      async queryFn({ managementId, data }) {
        try {
          const clinic = await updateNivClinicManagement(managementId, data);
          return { data: clinic };
        } catch (error) {
          console.error(
            "[CK-9905] Error in updateNivClinicManagement mutation:",
            {
              errorCode: "CK-9905",
              errorMessage: error.message,
              managementId,
              timestamp: new Date().toISOString(),
            }
          );
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { managementId }) => [
        { type: "NivClinic", id: managementId },
        { type: "NivClinic", id: "LIST" },
        "NivStats",
        "NivAlerts",
      ],
    }),

    // Update clinic status with history
    updateNivClinicStatus: builder.mutation({
      async queryFn({ managementId, newStatus, note }) {
        try {
          const result = await updateNivClinicStatus(
            managementId,
            newStatus,
            note
          );
          return { data: result };
        } catch (error) {
          console.error("[CK-9905] Error in updateNivClinicStatus mutation:", {
            errorCode: "CK-9905",
            errorMessage: error.message,
            managementId,
            newStatus,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { managementId }) => [
        { type: "NivClinic", id: managementId },
        { type: "NivClinic", id: "LIST" },
        "NivStats",
        "NivAlerts",
      ],
    }),

    // Add call log
    addNivCallLog: builder.mutation({
      async queryFn({ managementId, logData }) {
        try {
          const log = await addNivCallLog(managementId, logData);
          return { data: log };
        } catch (error) {
          console.error("[CK-9906] Error in addNivCallLog mutation:", {
            errorCode: "CK-9906",
            errorMessage: error.message,
            managementId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { managementId }) => [
        { type: "NivClinic", id: managementId },
      ],
    }),

    // Add payment record
    addNivPaymentRecord: builder.mutation({
      async queryFn({ managementId, paymentData }) {
        try {
          const payment = await addNivPaymentRecord(managementId, paymentData);
          return { data: payment };
        } catch (error) {
          console.error("[CK-9907] Error in addNivPaymentRecord mutation:", {
            errorCode: "CK-9907",
            errorMessage: error.message,
            managementId,
            paymentData: { month: paymentData?.month },
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { managementId }) => [
        { type: "NivClinic", id: managementId },
        { type: "NivClinic", id: "LIST" },
        "NivStats",
        "NivAlerts",
      ],
    }),

    // Update clinic SMS limit
    updateClinicSmsLimit: builder.mutation({
      async queryFn({ managementId, clinicId, newLimit }) {
        try {
          const result = await updateClinicSmsLimit(
            managementId,
            clinicId,
            newLimit
          );
          return { data: result };
        } catch (error) {
          console.error("[CK-9908] Error in updateClinicSmsLimit mutation:", {
            errorCode: "CK-9908",
            errorMessage: error.message,
            managementId,
            clinicId,
            newLimit,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { managementId, clinicId }) => [
        { type: "NivClinic", id: managementId },
        { type: "NivClinic", id: `usage_${clinicId}` },
        "NivAlerts",
      ],
    }),

    // ============================================
    // TASKS
    // ============================================

    // Get all NiV tasks
    getNivTasks: builder.query({
      async queryFn({ status, limit: queryLimit } = {}) {
        try {
          const tasks = await getNivTasks({ status, limit: queryLimit });
          return { data: tasks };
        } catch (error) {
          console.error("[CK-9913] Error in getNivTasks query:", {
            errorCode: "CK-9913",
            errorMessage: error.message,
            status,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "NivTask", id })),
              { type: "NivTask", id: "LIST" },
            ]
          : [{ type: "NivTask", id: "LIST" }],
    }),

    // Get single task
    getNivTask: builder.query({
      async queryFn({ taskId }) {
        try {
          const task = await getNivTask(taskId);
          return { data: task };
        } catch (error) {
          console.error("[CK-9914] Error in getNivTask query:", {
            errorCode: "CK-9914",
            errorMessage: error.message,
            taskId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { taskId }) => [
        { type: "NivTask", id: taskId },
      ],
    }),

    // Create task
    createNivTask: builder.mutation({
      async queryFn({ taskData }) {
        try {
          const task = await createNivTask(taskData);
          return { data: task };
        } catch (error) {
          console.error("[CK-9910] Error in createNivTask mutation:", {
            errorCode: "CK-9910",
            errorMessage: error.message,
            taskData: { title: taskData?.title },
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "NivTask", id: "LIST" }, "NivStats"],
    }),

    // Update task
    updateNivTask: builder.mutation({
      async queryFn({ taskId, data }) {
        try {
          const task = await updateNivTask(taskId, data);
          return { data: task };
        } catch (error) {
          console.error("[CK-9911] Error in updateNivTask mutation:", {
            errorCode: "CK-9911",
            errorMessage: error.message,
            taskId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      // Optimistic update so inline edits (status/priority/type/title) feel instant
      async onQueryStarted({ taskId, data }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          nivApi.util.updateQueryData("getNivTasks", undefined, (draft) => {
            const task = draft?.find((t) => t.id === taskId);
            if (task) {
              Object.assign(task, data);
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (result, error, { taskId }) => [
        { type: "NivTask", id: taskId },
        { type: "NivTask", id: "LIST" },
        "NivStats",
      ],
    }),

    // Delete task
    deleteNivTask: builder.mutation({
      async queryFn({ taskId }) {
        try {
          await deleteNivTask(taskId);
          return { data: { id: taskId } };
        } catch (error) {
          console.error("[CK-9912] Error in deleteNivTask mutation:", {
            errorCode: "CK-9912",
            errorMessage: error.message,
            taskId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "NivTask", id: "LIST" }, "NivStats"],
    }),

    // Get tasks board config (lists + option sets)
    getNivTasksConfig: builder.query({
      async queryFn() {
        try {
          const config = await getNivTasksConfig();
          return { data: config };
        } catch (error) {
          console.error("[CK-9918] Error in getNivTasksConfig query:", {
            errorCode: "CK-9918",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: ["NivTasksConfig"],
    }),

    // Update tasks board config
    updateNivTasksConfig: builder.mutation({
      async queryFn({ data }) {
        try {
          const config = await updateNivTasksConfig(data);
          return { data: config };
        } catch (error) {
          console.error("[CK-9919] Error in updateNivTasksConfig mutation:", {
            errorCode: "CK-9919",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      // Optimistic config update (collapse toggles, option edits feel instant)
      async onQueryStarted({ data }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          nivApi.util.updateQueryData("getNivTasksConfig", undefined, (draft) => {
            if (draft) {
              Object.assign(draft, data);
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: ["NivTasksConfig"],
    }),

    // Bulk update tasks (reassign option/list before deletion)
    bulkUpdateNivTasks: builder.mutation({
      async queryFn({ taskIds, data }) {
        try {
          const result = await bulkUpdateNivTasks(taskIds, data);
          return { data: result };
        } catch (error) {
          console.error("[CK-9934] Error in bulkUpdateNivTasks mutation:", {
            errorCode: "CK-9934",
            errorMessage: error.message,
            taskIds,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "NivTask", id: "LIST" }],
    }),

    // ============================================
    // ONBOARDING REQUESTS
    // ============================================

    // Get all onboarding call requests
    getOnboardingRequests: builder.query({
      async queryFn() {
        try {
          const requests = await getOnboardingRequests();
          return { data: requests };
        } catch (error) {
          console.error("[CK-9922] Error in getOnboardingRequests query:", {
            errorCode: "CK-9922",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: ["NivOnboarding"],
    }),

    // Mark onboarding as completed
    markOnboardingCompleted: builder.mutation({
      async queryFn({ clinicId }) {
        try {
          const result = await markOnboardingCompleted(clinicId);
          return { data: result };
        } catch (error) {
          console.error(
            "[CK-9923] Error in markOnboardingCompleted mutation:",
            {
              errorCode: "CK-9923",
              errorMessage: error.message,
              clinicId,
              timestamp: new Date().toISOString(),
            }
          );
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: ["NivOnboarding"],
    }),

    // ============================================
    // CHURN RISK RESOLUTIONS
    // ============================================

    // Get resolved (handled) churn alerts
    // ============================================
    // MONITORING ALERTS (niv_alerts — dailyAdminMonitoring cron output)
    // ============================================

    getMonitoringAlerts: builder.query({
      async queryFn() {
        try {
          const cards = await getMonitoringAlerts();
          return { data: cards };
        } catch (error) {
          console.error("[CK-7952] Error in getMonitoringAlerts query:", {
            errorCode: "CK-7952",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: ["NivMonitoringAlerts"],
    }),

    snoozeMonitoringAlert: builder.mutation({
      async queryFn({ card, days }) {
        try {
          await snoozeMonitoringAlert(card, days || 7);
          return { data: { clinicId: card?.id, days: days || 7 } };
        } catch (error) {
          console.error("[CK-7954] Error in snoozeMonitoringAlert mutation:", {
            errorCode: "CK-7954",
            errorMessage: error.message,
            clinicId: card?.id,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: ["NivMonitoringAlerts"],
    }),

    snoozeMonitoringAlertsBulk: builder.mutation({
      async queryFn({ cards, days }) {
        try {
          await snoozeMonitoringAlertsBulk(cards || [], days || 7);
          return { data: { count: (cards || []).length } };
        } catch (error) {
          console.error("[CK-7956] Error in snoozeMonitoringAlertsBulk mutation:", {
            errorCode: "CK-7956",
            errorMessage: error.message,
            count: (cards || []).length,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: ["NivMonitoringAlerts"],
    }),

    // ============================================
    // LENNY (AI SUPPORT) — REVIEW QUEUE + CLINIC HISTORY
    // ============================================

    getLennyReviews: builder.query({
      async queryFn(options = {}) {
        try {
          const reviews = await getLennyReviews(options);
          return { data: reviews };
        } catch (error) {
          console.error("[CK-7974] Error in getLennyReviews query:", {
            errorCode: "CK-7974",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: ["NivLennyReviews"],
    }),

    setLennyReviewStatus: builder.mutation({
      async queryFn({ reviewId, status, resolvedBy }) {
        try {
          await setLennyReviewStatus(reviewId, { status, resolvedBy });
          return { data: { reviewId, status } };
        } catch (error) {
          console.error("[CK-7975] Error in setLennyReviewStatus mutation:", {
            errorCode: "CK-7975",
            errorMessage: error.message,
            reviewId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: ["NivLennyReviews", "NivLennyHistory"],
    }),

    getClinicLennyHistory: builder.query({
      async queryFn({ clinicId }) {
        try {
          const messages = await getClinicLennyHistory(clinicId);
          return { data: messages };
        } catch (error) {
          console.error("[CK-7976] Error in getClinicLennyHistory query:", {
            errorCode: "CK-7976",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { clinicId }) => [
        { type: "NivLennyHistory", id: clinicId },
        "NivLennyHistory",
      ],
    }),

    getChurnResolutions: builder.query({
      async queryFn() {
        try {
          const resolutions = await getChurnResolutions();
          return { data: resolutions };
        } catch (error) {
          console.error("[CK-9928] Error in getChurnResolutions query:", {
            errorCode: "CK-9928",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: ["NivChurnResolution"],
    }),

    // Mark a churn alert as resolved/handled
    resolveChurnAlert: builder.mutation({
      async queryFn({ clinicId, level }) {
        try {
          const result = await resolveChurnAlert(clinicId, level);
          return { data: result };
        } catch (error) {
          console.error("[CK-9929] Error in resolveChurnAlert mutation:", {
            errorCode: "CK-9929",
            errorMessage: error.message,
            clinicId,
            level,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: ["NivChurnResolution"],
    }),

    // ============================================
    // STAFF THERAPIST ADDITIONS
    // ============================================

    // Get all staff-therapist addition alerts
    getTherapistAdditions: builder.query({
      async queryFn() {
        try {
          const additions = await getTherapistAdditions();
          return { data: additions };
        } catch (error) {
          console.error("[CK-9883] Error in getTherapistAdditions query:", {
            errorCode: "CK-9883",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "NivTherapistAddition", id })),
              { type: "NivTherapistAddition", id: "LIST" },
            ]
          : [{ type: "NivTherapistAddition", id: "LIST" }],
    }),

    // Mark a staff-therapist addition as resolved
    resolveTherapistAddition: builder.mutation({
      async queryFn({ additionId }) {
        try {
          const result = await resolveTherapistAddition(additionId);
          return { data: result };
        } catch (error) {
          console.error("[CK-9884] Error in resolveTherapistAddition mutation:", {
            errorCode: "CK-9884",
            errorMessage: error.message,
            additionId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { additionId }) => [
        { type: "NivTherapistAddition", id: additionId },
        { type: "NivTherapistAddition", id: "LIST" },
      ],
    }),

    // ============================================
    // CALL REQUESTS
    // ============================================

    // Get all call requests
    getCallRequests: builder.query({
      async queryFn() {
        try {
          const requests = await getCallRequests();
          return { data: requests };
        } catch (error) {
          console.error("[CK-9924] Error in getCallRequests query:", {
            errorCode: "CK-9924",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "NivCallRequest", id })),
              { type: "NivCallRequest", id: "LIST" },
            ]
          : [{ type: "NivCallRequest", id: "LIST" }],
    }),

    // Create call request
    createCallRequest: builder.mutation({
      async queryFn({ requestData }) {
        try {
          const request = await createCallRequest(requestData);
          return { data: request };
        } catch (error) {
          console.error("[CK-9925] Error in createCallRequest mutation:", {
            errorCode: "CK-9925",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "NivCallRequest", id: "LIST" }],
    }),

    // Create lead from public page (homepage / get-started). Unauthenticated.
    createLeadFromLandingPage: builder.mutation({
      async queryFn(leadData) {
        try {
          const lead = await createPublicLead(leadData);
          return { data: lead };
        } catch (error) {
          console.error("[CK-1051] Error in createLeadFromLandingPage mutation:", {
            errorCode: "CK-1051",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "NivCallRequest", id: "LIST" }],
    }),

    // Update call request status
    updateCallRequestStatus: builder.mutation({
      async queryFn({ requestId, status, notes }) {
        try {
          const result = await updateCallRequestStatus(requestId, status, notes);
          return { data: result };
        } catch (error) {
          console.error("[CK-9926] Error in updateCallRequestStatus mutation:", {
            errorCode: "CK-9926",
            errorMessage: error.message,
            requestId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { requestId }) => [
        { type: "NivCallRequest", id: requestId },
        { type: "NivCallRequest", id: "LIST" },
      ],
    }),

    // Delete call request
    deleteCallRequest: builder.mutation({
      async queryFn({ requestId }) {
        try {
          await deleteCallRequest(requestId);
          return { data: { id: requestId } };
        } catch (error) {
          console.error("[CK-9927] Error in deleteCallRequest mutation:", {
            errorCode: "CK-9927",
            errorMessage: error.message,
            requestId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "NivCallRequest", id: "LIST" }],
    }),

    // Add a conversation-update note to a call request
    addCallRequestNote: builder.mutation({
      async queryFn({ requestId, text }) {
        try {
          const result = await addCallRequestNote(requestId, text);
          return { data: result };
        } catch (error) {
          console.error("[CK-9928] Error in addCallRequestNote mutation:", {
            errorCode: "CK-9928",
            errorMessage: error.message,
            requestId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "NivCallRequest", id: "LIST" }],
    }),

    // ============================================
    // PLANS MANAGEMENT (Using SYSTEM_PLANS - hard-coded)
    // ============================================

    // Get all system plans (hard-coded, no database fetch needed)
    getNivPlans: builder.query({
      async queryFn() {
        try {
          // Return hard-coded SYSTEM_PLANS instead of fetching from database
          const plans = getSystemPlansArray();
          return { data: plans };
        } catch (error) {
          console.error("[CK-9930] Error in getNivPlans query:", {
            errorCode: "CK-9930",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: "NivPlan", id: "LIST" }],
    }),

    // Get system plans with full details
    getSystemPlans: builder.query({
      queryFn() {
        return { data: SYSTEM_PLANS };
      },
      providesTags: [{ type: "NivPlan", id: "SYSTEM" }],
    }),

    // Legacy: Create plan - kept for backward compatibility but warns
    createNivPlan: builder.mutation({
      async queryFn({ planData }) {
        console.warn("[DEPRECATED] createNivPlan is deprecated. System plans are hard-coded.");
        try {
          const plan = await createNivPlan(planData);
          return { data: plan };
        } catch (error) {
          console.error("[CK-9931] Error in createNivPlan mutation:", {
            errorCode: "CK-9931",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "NivPlan", id: "LIST" }],
    }),

    // Legacy: Update plan - kept for backward compatibility but warns
    updateNivPlan: builder.mutation({
      async queryFn({ planId, data }) {
        console.warn("[DEPRECATED] updateNivPlan is deprecated. System plans are hard-coded.");
        try {
          const plan = await updateNivPlan(planId, data);
          return { data: plan };
        } catch (error) {
          console.error("[CK-9932] Error in updateNivPlan mutation:", {
            errorCode: "CK-9932",
            errorMessage: error.message,
            planId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { planId }) => [
        { type: "NivPlan", id: planId },
        { type: "NivPlan", id: "LIST" },
        { type: "NivClinic", id: "LIST" },
      ],
    }),

    // Legacy: Delete plan - kept for backward compatibility but warns
    deleteNivPlan: builder.mutation({
      async queryFn({ planId }) {
        console.warn("[DEPRECATED] deleteNivPlan is deprecated. System plans cannot be deleted.");
        try {
          await deleteNivPlan(planId);
          return { data: { id: planId } };
        } catch (error) {
          console.error("[CK-9933] Error in deleteNivPlan mutation:", {
            errorCode: "CK-9933",
            errorMessage: error.message,
            planId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "NivPlan", id: "LIST" }],
    }),

    // Legacy: Seed default plans - no longer needed
    seedDefaultPlans: builder.mutation({
      async queryFn() {
        // No-op since plans are now hard-coded
        return { data: { seeded: false, message: "Plans are hard-coded" } };
      },
      invalidatesTags: [{ type: "NivPlan", id: "LIST" }],
    }),

    // NEW: Update clinic plan directly (updates actual clinic.plan field)
    updateClinicPlanDirectly: builder.mutation({
      async queryFn({ clinicId, planCode, customPayment }) {
        try {
          const result = await updateClinicPlanDirectly(
            clinicId,
            planCode,
            customPayment
          );
          return { data: result };
        } catch (error) {
          console.error("[CK-9944] Error in updateClinicPlanDirectly mutation:", {
            errorCode: "CK-9944",
            errorMessage: error.message,
            clinicId,
            planCode,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { clinicId }) => [
        { type: "NivClinic", id: clinicId },
        { type: "NivClinic", id: "LIST" },
        "NivStats",
        "NivClinicsOverview",
      ],
    }),

    // NEW: Set (or clear) clinic plan end date → auto-revert to trial when passed
    setClinicPlanEndDate: builder.mutation({
      async queryFn({ clinicId, planEndDate }) {
        try {
          const result = await setClinicPlanEndDate(clinicId, planEndDate);
          return { data: result };
        } catch (error) {
          console.error("[CK-9948] Error in setClinicPlanEndDate mutation:", {
            errorCode: "CK-9948",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { clinicId }) => [
        { type: "NivClinic", id: clinicId },
        { type: "NivClinic", id: "LIST" },
        "NivClinicsOverview",
      ],
    }),

    // NEW: Update custom payment only
    updateClinicCustomPayment: builder.mutation({
      async queryFn({ clinicId, customPayment }) {
        try {
          const result = await updateClinicCustomPayment(clinicId, customPayment);
          return { data: result };
        } catch (error) {
          console.error("[CK-9945] Error in updateClinicCustomPayment mutation:", {
            errorCode: "CK-9945",
            errorMessage: error.message,
            clinicId,
            customPayment,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "NivClinic", id: "LIST" }, "NivStats"],
    }),

    // ============================================
    // SYNC & REVENUE
    // ============================================

    // Sync all clinics to management
    syncClinicsToManagement: builder.mutation({
      async queryFn() {
        try {
          const result = await syncAllClinicsToManagement();
          return { data: result };
        } catch (error) {
          console.error(
            "[CK-9940] Error in syncClinicsToManagement mutation:",
            {
              errorCode: "CK-9940",
              errorMessage: error.message,
              timestamp: new Date().toISOString(),
            }
          );
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "NivClinic", id: "LIST" }, "NivStats"],
    }),

    // Update clinic plan
    updateClinicPlan: builder.mutation({
      async queryFn({ managementId, planId, customPayment }) {
        try {
          const result = await updateClinicPlan(
            managementId,
            planId,
            customPayment
          );
          return { data: result };
        } catch (error) {
          console.error("[CK-9941] Error in updateClinicPlan mutation:", {
            errorCode: "CK-9941",
            errorMessage: error.message,
            managementId,
            planId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { managementId }) => [
        { type: "NivClinic", id: managementId },
        { type: "NivClinic", id: "LIST" },
        "NivStats",
      ],
    }),

    // Get total monthly revenue
    getTotalMonthlyRevenue: builder.query({
      async queryFn() {
        try {
          const result = await getTotalMonthlyRevenue();
          return { data: result };
        } catch (error) {
          console.error("[CK-9942] Error in getTotalMonthlyRevenue query:", {
            errorCode: "CK-9942",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: ["NivStats"],
    }),

    // ============================================
    // SYSTEM NOTIFICATIONS
    // ============================================

    getSystemNotifications: builder.query({
      async queryFn() {
        try {
          const notifications = await getSystemNotifications(200);
          return { data: notifications };
        } catch (error) {
          console.error("[CK-9977] Error in getSystemNotifications query:", {
            errorCode: "CK-9977",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: ["NivSystemNotifications"],
    }),

    deleteSystemNotification: builder.mutation({
      async queryFn({ notifId }) {
        try {
          await deleteSystemNotification(notifId);
          return { data: { success: true } };
        } catch (error) {
          console.error("[CK-9978] Error in deleteSystemNotification mutation:", {
            errorCode: "CK-9978",
            errorMessage: error.message,
            notifId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: ["NivSystemNotifications"],
    }),

    // ============================================
    // FOOD PROMOTION REQUESTS (nutrition clinics)
    // ============================================

    getFoodPromotionRequests: builder.query({
      async queryFn({ status = "pending" } = {}) {
        try {
          const requests = await getFoodPromotionRequests(status);
          return { data: requests };
        } catch (error) {
          console.error("[CK-7828] Error in getFoodPromotionRequests query:", {
            errorCode: "CK-7828",
            errorMessage: error.message,
            status,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: ["NivFoodRequests"],
    }),

    approveFoodPromotionRequest: builder.mutation({
      async queryFn({ requestId, overrides }) {
        try {
          const result = await approveFoodPromotionRequest(requestId, overrides);
          return { data: result };
        } catch (error) {
          console.error("[CK-7829] Error in approveFoodPromotionRequest mutation:", {
            errorCode: "CK-7829",
            errorMessage: error.message,
            requestId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: ["NivFoodRequests"],
    }),

    rejectFoodPromotionRequest: builder.mutation({
      async queryFn({ requestId, reason }) {
        try {
          const result = await rejectFoodPromotionRequest(requestId, reason);
          return { data: result };
        } catch (error) {
          console.error("[CK-7830] Error in rejectFoodPromotionRequest mutation:", {
            errorCode: "CK-7830",
            errorMessage: error.message,
            requestId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: ["NivFoodRequests"],
    }),

    // Seed the global `foods` catalog from the export bundled with the Cloud
    // Functions. Dry run unless `dryRun: false` is passed explicitly; the
    // server refuses to commit when the export looks like it changed shape.
    seedFoodCatalog: builder.mutation({
      async queryFn({ dryRun = true } = {}) {
        try {
          const fn = httpsCallable(functions, "seedFoodCatalog");
          const result = await fn({ dryRun });
          return { data: result.data };
        } catch (error) {
          console.error("[CK-7831] Error in seedFoodCatalog mutation:", {
            errorCode: "CK-7831",
            errorMessage: error.message,
            dryRun,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      // A commit changes the global catalog, but the food pickers read it
      // through `nutritionFoodsApi` — tags do not cross API slices, so the
      // caller refreshes that cache itself (see NivFoodRequestsTool).
      invalidatesTags: (result) =>
        result?.mode === "commit" ? ["NivFoodRequests"] : [],
    }),

    // Hard-delete a clinic (test-clinic cleanup). Server-side only: removes
    // the clinic tree, its therapists + Auth users, NiV side-tables and
    // Storage files. Requires the typed clinic name as a second guard.
    deleteClinicHard: builder.mutation({
      async queryFn({ clinicId, confirmName }) {
        try {
          const fn = httpsCallable(functions, "nivDeleteClinic");
          const result = await fn({ clinicId, confirmName });
          return { data: result.data };
        } catch (error) {
          console.error("[CK-7965] Error in deleteClinicHard mutation:", {
            errorCode: "CK-7965",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [
        "NivClinic",
        "NivStats",
        "NivAlerts",
        "NivMonitoringAlerts",
        "NivClinicsOverview",
        "NivClinicTags",
        "NivClinicsActivation",
        "NivTelemetry",
        "NivUsage",
        "NivChurnResolution",
        "NivTherapistAddition",
      ],
    }),

    // ============================================
    // SYSTEM MESSAGES (broadcasts -> entry popups)
    // ============================================
    getNivSystemMessages: builder.query({
      async queryFn() {
        try {
          const messages = await getNivSystemMessages();
          return { data: messages };
        } catch (error) {
          console.error("[CK-9985] Error in getNivSystemMessages query:", {
            errorCode: "CK-9985",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: ["NivSystemMessages"],
    }),

    createNivSystemMessage: builder.mutation({
      async queryFn(payload) {
        try {
          const result = await createNivSystemMessage(payload);
          return { data: result };
        } catch (error) {
          console.error("[CK-9986] Error in createNivSystemMessage mutation:", {
            errorCode: "CK-9986",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: ["NivSystemMessages"],
    }),

    deleteNivSystemMessage: builder.mutation({
      async queryFn({ messageId }) {
        try {
          await deleteNivSystemMessage(messageId);
          return { data: { success: true } };
        } catch (error) {
          console.error("[CK-9987] Error in deleteNivSystemMessage mutation:", {
            errorCode: "CK-9987",
            errorMessage: error.message,
            messageId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: ["NivSystemMessages"],
    }),

    // ============================================
    // REGISTRATION WHITELIST
    // ============================================
    getRegistrationWhitelist: builder.query({
      async queryFn() {
        try {
          const entries = await getRegistrationWhitelist();
          return { data: entries };
        } catch (error) {
          console.error("[CK-9054] Error in getRegistrationWhitelist query:", {
            errorCode: "CK-9054",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: ["RegistrationWhitelist"],
    }),

    addPhoneToWhitelist: builder.mutation({
      async queryFn(payload) {
        try {
          const entry = await addPhoneToWhitelist(payload);
          return { data: entry };
        } catch (error) {
          console.error("[CK-9055] Error in addPhoneToWhitelist mutation:", {
            errorCode: "CK-9055",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      // Leads board derives isWhitelisted from this collection
      invalidatesTags: ["RegistrationWhitelist", "NivProspect"],
    }),

    removePhoneFromWhitelist: builder.mutation({
      async queryFn(payload) {
        try {
          await removePhoneFromWhitelist(payload);
          return { data: { phone: payload.phone } };
        } catch (error) {
          console.error("[CK-9056] Error in removePhoneFromWhitelist mutation:", {
            errorCode: "CK-9056",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: ["RegistrationWhitelist", "NivProspect"],
    }),

    // ============================================
    // CLINIC CRM (MANUAL ADMIN LAYER)
    // ============================================
    getNivClinicsOverview: builder.query({
      async queryFn() {
        try {
          const rows = await getNivClinicsOverview();
          return { data: rows };
        } catch (error) {
          console.error("[CK-7947] Error in getNivClinicsOverview query:", {
            errorCode: "CK-7947",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: ["NivClinicsOverview"],
    }),

    updateNivClinicCrm: builder.mutation({
      async queryFn({ clinicId, updates }) {
        try {
          const result = await updateNivClinicCrm(clinicId, updates);
          return { data: result };
        } catch (error) {
          console.error("[CK-7948] Error in updateNivClinicCrm mutation:", {
            errorCode: "CK-7948",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { clinicId }) => [
        "NivClinicsOverview",
        { type: "NivClinic", id: clinicId },
      ],
    }),

    addNivCrmPayment: builder.mutation({
      async queryFn({ clinicId, payment }) {
        try {
          const result = await addNivCrmPayment(clinicId, payment);
          return { data: result };
        } catch (error) {
          console.error("[CK-7949] Error in addNivCrmPayment mutation:", {
            errorCode: "CK-7949",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { clinicId }) => [
        "NivClinicsOverview",
        { type: "NivClinic", id: clinicId },
      ],
    }),

    // Payment + optional planEndDate extension in one confirmed flow.
    // Payment always commits first; a date failure surfaces as
    // data.planEndDateError (the payment itself succeeded).
    addNivCrmPaymentAndExtend: builder.mutation({
      async queryFn({ clinicId, payment, newPlanEndDate }) {
        try {
          const result = await addNivCrmPaymentAndExtend(clinicId, payment, newPlanEndDate || null);
          return { data: result };
        } catch (error) {
          console.error("[CK-7955] Error in addNivCrmPaymentAndExtend mutation:", {
            errorCode: "CK-7955",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { clinicId }) => [
        "NivClinicsOverview",
        { type: "NivClinic", id: clinicId },
      ],
    }),

    deleteNivCrmPayment: builder.mutation({
      async queryFn({ clinicId, paymentId }) {
        try {
          const result = await deleteNivCrmPayment(clinicId, paymentId);
          return { data: result };
        } catch (error) {
          console.error("[CK-7950] Error in deleteNivCrmPayment mutation:", {
            errorCode: "CK-7950",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { clinicId }) => [
        "NivClinicsOverview",
        { type: "NivClinic", id: clinicId },
      ],
    }),

    addNivCrmNote: builder.mutation({
      async queryFn({ clinicId, text, author }) {
        try {
          const result = await addNivCrmNote(clinicId, { text, author });
          return { data: result };
        } catch (error) {
          console.error("[CK-7951] Error in addNivCrmNote mutation:", {
            errorCode: "CK-7951",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { clinicId }) => [
        "NivClinicsOverview",
        { type: "NivClinic", id: clinicId },
      ],
    }),

    // ============================================
    // CLINIC TAGS + CONTACT TRACKING + ACTIVATION (clinics tab)
    // Error codes: CK-7958, CK-7959, CK-7980 - CK-7989
    // Tag/contact mutations patch the overview cache optimistically and
    // deliberately do NOT invalidate "NivClinicsOverview" (that refetch
    // re-reads every clinic's patients subcollection).
    // ============================================

    getNivClinicTagsRegistry: builder.query({
      async queryFn() {
        try {
          const result = await getNivClinicTagsRegistry();
          return { data: result };
        } catch (error) {
          console.error("[CK-7958] Error in getNivClinicTagsRegistry query:", {
            errorCode: "CK-7958",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // The one-time starred→tag migration wrote tagIds on CRM docs the
          // overview already cached — refresh it once.
          if (data?.migrated > 0) {
            dispatch(nivApi.util.invalidateTags(["NivClinicsOverview"]));
          }
        } catch {
          /* handled in queryFn */
        }
      },
      providesTags: ["NivClinicTags"],
    }),

    saveNivClinicTag: builder.mutation({
      async queryFn({ id, label, color }) {
        try {
          const result = await saveNivClinicTag({ id, label, color });
          return { data: result };
        } catch (error) {
          console.error("[CK-7959] Error in saveNivClinicTag mutation:", {
            errorCode: "CK-7959",
            errorMessage: error.message,
            tagId: id || null,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: ["NivClinicTags"],
    }),

    deleteNivClinicTag: builder.mutation({
      async queryFn({ tagId }) {
        try {
          const result = await deleteNivClinicTag(tagId);
          return { data: result };
        } catch (error) {
          console.error("[CK-7980] Error in deleteNivClinicTag mutation:", {
            errorCode: "CK-7980",
            errorMessage: error.message,
            tagId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: ["NivClinicTags", "NivClinicsOverview"],
    }),

    setNivClinicTagIds: builder.mutation({
      async queryFn({ clinicId, tagIds }) {
        try {
          const result = await setNivClinicTagIds(clinicId, tagIds);
          return { data: result };
        } catch (error) {
          console.error("[CK-7981] Error in setNivClinicTagIds mutation:", {
            errorCode: "CK-7981",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      async onQueryStarted({ clinicId, tagIds }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          nivApi.util.updateQueryData("getNivClinicsOverview", undefined, (draft) => {
            const row = draft.find((r) => r.clinicId === clinicId);
            if (row) row.crm = { ...(row.crm || {}), tagIds: [...tagIds] };
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (result, error, { clinicId }) => [{ type: "NivClinic", id: clinicId }],
    }),

    addNivCrmContact: builder.mutation({
      async queryFn({ clinicId, contact }) {
        try {
          const result = await addNivCrmContact(clinicId, contact);
          return { data: result };
        } catch (error) {
          console.error("[CK-7982] Error in addNivCrmContact mutation:", {
            errorCode: "CK-7982",
            errorMessage: error.message,
            clinicId,
            channel: contact?.channel,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      async onQueryStarted({ clinicId, contact }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          nivApi.util.updateQueryData("getNivClinicsOverview", undefined, (draft) => {
            const row = draft.find((r) => r.clinicId === clinicId);
            if (!row) return;
            const crm = row.crm || {};
            const prevLast = crm.lastContactAt ? new Date(crm.lastContactAt) : null;
            const next = new Date(contact.date || Date.now());
            row.crm = {
              ...crm,
              contacts: [...(crm.contacts || []), { ...contact }],
              lastContactAt:
                prevLast && prevLast > next ? crm.lastContactAt : next.toISOString(),
              contactDismissedKey: null,
              contactDismissedAt: null,
            };
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (result, error, { clinicId }) => [{ type: "NivClinic", id: clinicId }],
    }),

    deleteNivCrmContact: builder.mutation({
      async queryFn({ clinicId, contactId }) {
        try {
          const result = await deleteNivCrmContact(clinicId, contactId);
          return { data: result };
        } catch (error) {
          console.error("[CK-7983] Error in deleteNivCrmContact mutation:", {
            errorCode: "CK-7983",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      async onQueryStarted({ clinicId, contactId }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          nivApi.util.updateQueryData("getNivClinicsOverview", undefined, (draft) => {
            const row = draft.find((r) => r.clinicId === clinicId);
            if (!row) return;
            const contacts = (row.crm?.contacts || []).filter((c) => c.id !== contactId);
            const lastContactAt = contacts.reduce(
              (max, c) => (!max || new Date(c.date) > new Date(max) ? c.date : max),
              null
            );
            row.crm = { ...(row.crm || {}), contacts, lastContactAt };
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (result, error, { clinicId }) => [{ type: "NivClinic", id: clinicId }],
    }),

    dismissNivContactNeed: builder.mutation({
      async queryFn({ clinicId, dismissKey }) {
        try {
          const result = await dismissNivContactNeed(clinicId, dismissKey);
          return { data: result };
        } catch (error) {
          console.error("[CK-7984] Error in dismissNivContactNeed mutation:", {
            errorCode: "CK-7984",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      async onQueryStarted({ clinicId, dismissKey }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          nivApi.util.updateQueryData("getNivClinicsOverview", undefined, (draft) => {
            const row = draft.find((r) => r.clinicId === clinicId);
            if (!row) return;
            row.crm = {
              ...(row.crm || {}),
              contactDismissedKey: dismissKey,
              contactDismissedAt: new Date().toISOString(),
            };
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (result, error, { clinicId }) => [{ type: "NivClinic", id: clinicId }],
    }),

    // Activation counts (appointments via count() aggregates). Arg is the
    // sorted clinicIds joined by "," for a stable cache key. Cached 15 min;
    // never invalidated by CRM mutations.
    getClinicsActivation: builder.query({
      async queryFn(clinicIdsKey) {
        try {
          const ids = clinicIdsKey ? clinicIdsKey.split(",").filter(Boolean) : [];
          const result = await getClinicsActivationCounts(ids);
          return { data: result };
        } catch (error) {
          console.error("[CK-7985] Error in getClinicsActivation query:", {
            errorCode: "CK-7985",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      keepUnusedDataFor: 900,
      providesTags: ["NivClinicsActivation"],
    }),

    // ============================================
    // PROSPECTS (UNIFIED CRM PIPELINE)
    // ============================================
    getProspectsBoard: builder.query({
      async queryFn() {
        try {
          const board = await getNivProspectsBoard();
          return { data: board };
        } catch (error) {
          console.error("[CK-7940] Error in getProspectsBoard query:", {
            errorCode: "CK-7940",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: ["NivProspect"],
    }),

    createProspect: builder.mutation({
      async queryFn(payload) {
        try {
          const prospect = await createProspect(payload);
          return { data: prospect };
        } catch (error) {
          console.error("[CK-7941] Error in createProspect mutation:", {
            errorCode: "CK-7941",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: ["NivProspect"],
    }),

    updateProspectDetails: builder.mutation({
      async queryFn(payload) {
        try {
          const result = await updateProspectDetails(payload);
          return { data: result };
        } catch (error) {
          console.error("[CK-7942] Error in updateProspectDetails mutation:", {
            errorCode: "CK-7942",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: ["NivProspect"],
    }),

    dismissStaleApprovedProspect: builder.mutation({
      async queryFn(payload) {
        try {
          const result = await dismissStaleApprovedProspect(payload);
          return { data: result };
        } catch (error) {
          console.error("[CK-7957] Error in dismissStaleApprovedProspect mutation:", {
            errorCode: "CK-7957",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: ["NivProspect"],
    }),

    updateProspectStage: builder.mutation({
      async queryFn(payload) {
        try {
          const result = await updateProspectStage(payload);
          return { data: result };
        } catch (error) {
          console.error("[CK-7943] Error in updateProspectStage mutation:", {
            errorCode: "CK-7943",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: ["NivProspect"],
    }),

    addProspectNote: builder.mutation({
      async queryFn(payload) {
        try {
          const result = await addProspectNote(payload);
          return { data: result };
        } catch (error) {
          console.error("[CK-7944] Error in addProspectNote mutation:", {
            errorCode: "CK-7944",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: ["NivProspect"],
    }),

    approveProspectRegistration: builder.mutation({
      async queryFn(payload) {
        try {
          const result = await approveProspectRegistration(payload);
          return { data: result };
        } catch (error) {
          console.error("[CK-7945] Error in approveProspectRegistration mutation:", {
            errorCode: "CK-7945",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: ["NivProspect", "RegistrationWhitelist"],
    }),

    revokeProspectRegistration: builder.mutation({
      async queryFn(payload) {
        try {
          const result = await revokeProspectRegistration(payload);
          return { data: result };
        } catch (error) {
          console.error("[CK-7953] Error in revokeProspectRegistration mutation:", {
            errorCode: "CK-7953",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: ["NivProspect", "RegistrationWhitelist"],
    }),

    deleteProspect: builder.mutation({
      async queryFn(payload) {
        try {
          const result = await deleteProspect(payload);
          return { data: result };
        } catch (error) {
          console.error("[CK-7946] Error in deleteProspect mutation:", {
            errorCode: "CK-7946",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: ["NivProspect", "RegistrationWhitelist"],
    }),

    // ---- Leads CRM (Oct 2026): manual clinic link, free notes, AI summary ----
    // Error codes: CK-7933 - CK-7939

    linkProspectToClinic: builder.mutation({
      async queryFn(payload) {
        try {
          const result = await linkProspectToClinic(payload);
          return { data: result };
        } catch (error) {
          console.error("[CK-7933] Error in linkProspectToClinic mutation:", {
            errorCode: "CK-7933",
            errorMessage: error.message,
            clinicId: payload?.clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { clinicId }) => [
        "NivProspect",
        "NivClinicsOverview",
        { type: "NivClinic", id: clinicId },
      ],
    }),

    unlinkProspectFromClinic: builder.mutation({
      async queryFn(payload) {
        try {
          const result = await unlinkProspectFromClinic(payload);
          return { data: result };
        } catch (error) {
          console.error("[CK-7934] Error in unlinkProspectFromClinic mutation:", {
            errorCode: "CK-7934",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: ["NivProspect"],
    }),

    // Autosaved textarea — patch the board cache optimistically and do NOT
    // invalidate NivProspect (getProspectsBoard writes as it reads; refetching
    // it on every keystroke would be wasteful).
    updateProspectFreeNotes: builder.mutation({
      async queryFn(payload) {
        try {
          const result = await updateProspectFreeNotes(payload);
          return { data: result };
        } catch (error) {
          console.error("[CK-7935] Error in updateProspectFreeNotes mutation:", {
            errorCode: "CK-7935",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      async onQueryStarted({ phone, text }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          nivApi.util.updateQueryData("getProspectsBoard", undefined, (draft) => {
            const p = draft?.prospects?.find((x) => x.id === phone);
            if (!p) return;
            p.freeNotes = (text || "").trim() || null;
            p.freeNotesUpdatedAt = new Date().toISOString();
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),

    updateNivCrmFreeNotes: builder.mutation({
      async queryFn({ clinicId, text }) {
        try {
          const result = await updateNivCrmFreeNotes(clinicId, text);
          return { data: result };
        } catch (error) {
          console.error("[CK-7936] Error in updateNivCrmFreeNotes mutation:", {
            errorCode: "CK-7936",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      async onQueryStarted({ clinicId, text }, { dispatch, queryFulfilled }) {
        const value = (text || "").trim() || null;
        const now = new Date().toISOString();
        const patches = [
          dispatch(
            nivApi.util.updateQueryData("getNivClinicsOverview", undefined, (draft) => {
              const row = draft?.find((r) => r.clinicId === clinicId);
              if (!row) return;
              row.crm = { ...(row.crm || {}), freeNotes: value, freeNotesUpdatedAt: now };
            })
          ),
          dispatch(
            nivApi.util.updateQueryData("getNivClinicFullDetail", { clinicId }, (draft) => {
              if (!draft) return;
              draft.nivCrm = { ...(draft.nivCrm || {}), freeNotes: value, freeNotesUpdatedAt: now };
            })
          ),
        ];
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p.undo());
        }
      },
    }),

    // Next action (text + optional future reminder) — explicit save, so the
    // optimistic patch mirrors exactly what the helper persists. Not
    // invalidating NivProspect for the same reason as free notes.
    updateProspectNextAction: builder.mutation({
      async queryFn(payload) {
        try {
          const result = await updateProspectNextAction(payload);
          return { data: result };
        } catch (error) {
          console.error("[CK-7300] Error in updateProspectNextAction mutation:", {
            errorCode: "CK-7300",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      async onQueryStarted({ phone, text, at }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          nivApi.util.updateQueryData("getProspectsBoard", undefined, (draft) => {
            const p = draft?.prospects?.find((x) => x.id === phone);
            if (!p) return;
            p.nextAction = buildOptimisticNextAction(text, at);
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),

    updateNivCrmNextAction: builder.mutation({
      async queryFn({ clinicId, text, at }) {
        try {
          const result = await updateNivCrmNextAction(clinicId, { text, at });
          return { data: result };
        } catch (error) {
          console.error("[CK-7301] Error in updateNivCrmNextAction mutation:", {
            errorCode: "CK-7301",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      async onQueryStarted({ clinicId, text, at }, { dispatch, queryFulfilled }) {
        const next = buildOptimisticNextAction(text, at);
        const patches = [
          dispatch(
            nivApi.util.updateQueryData("getNivClinicsOverview", undefined, (draft) => {
              const row = draft?.find((r) => r.clinicId === clinicId);
              if (!row) return;
              row.crm = { ...(row.crm || {}), nextAction: next };
            })
          ),
          dispatch(
            nivApi.util.updateQueryData("getNivClinicFullDetail", { clinicId }, (draft) => {
              if (!draft) return;
              draft.nivCrm = { ...(draft.nivCrm || {}), nextAction: next };
            })
          ),
        ];
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p.undo());
        }
      },
    }),

    setStandingOrderMonthStatus: builder.mutation({
      async queryFn({ clinicId, month, action }) {
        try {
          const result = await setStandingOrderMonthStatus(clinicId, { month, action });
          return { data: result };
        } catch (error) {
          console.error("[CK-7937] Error in setStandingOrderMonthStatus mutation:", {
            errorCode: "CK-7937",
            errorMessage: error.message,
            clinicId,
            month,
            action,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { clinicId }) => [
        "NivClinicsOverview",
        { type: "NivClinic", id: clinicId },
      ],
    }),

    // WhatsApp chat export → AI client summary (server writes aiSummary on the
    // target doc: niv_prospects/{phone} or niv_clinic_crm/{clinicId}).
    generateNivChatSummary: builder.mutation({
      async queryFn({ target, chatText, fileName }) {
        try {
          const fn = httpsCallable(functions, "nivChatSummary", { timeout: 180000 });
          const result = await fn({ target, chatText, fileName });
          return { data: result.data };
        } catch (error) {
          console.error("[CK-7938] Error in generateNivChatSummary mutation:", {
            errorCode: "CK-7938",
            errorMessage: error.message,
            targetKind: target?.kind,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { target }) =>
        target?.kind === "clinic"
          ? ["NivClinicsOverview", { type: "NivClinic", id: target.id }]
          : ["NivProspect"],
    }),

    // Printable list (clinics tab / leads page) → landscape PDF URL. The
    // client sends the table it already shows; nothing to invalidate.
    nivPrintListPdf: builder.mutation({
      async queryFn(payload) {
        try {
          const fn = httpsCallable(functions, "nivPrintListPdf", { timeout: 120000 });
          const result = await fn(payload);
          return { data: result.data };
        } catch (error) {
          console.error("[CK-7313] Error in nivPrintListPdf mutation:", {
            errorCode: "CK-7313",
            errorMessage: error.message,
            kind: payload?.kind,
            rowCount: payload?.rows?.length,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
    }),

    // ============================================
    // TELEMETRY & SCORES (new analytics pipeline)
    // ============================================

    getTelemetryOverview: builder.query({
      async queryFn({ days = 30 } = {}) {
        try {
          const overview = await getTelemetryOverview({ days });
          return { data: serializeForRedux(overview) };
        } catch (error) {
          console.error("[CK-7910] Error fetching telemetry overview:", {
            errorCode: "CK-7910",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: "NivTelemetry", id: "OVERVIEW" }],
    }),

    // Public marketing-page traffic (niv_public_pageviews via nivPublicAnalytics):
    // views / unique visitors / referrers / devices / lead insights. days ∈ {7,30,90}.
    getPublicAnalytics: builder.query({
      async queryFn({ days = 30 } = {}) {
        try {
          const fn = httpsCallable(functions, "nivPublicAnalytics");
          const res = await fn({ days });
          return { data: res.data };
        } catch (error) {
          console.error("[CK-7999] Error in getPublicAnalytics query:", {
            errorCode: "CK-7999",
            errorMessage: error.message,
            days,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      keepUnusedDataFor: 300,
      providesTags: (result, error, arg) => [{ type: "NivTelemetry", id: `PUBLIC_${arg?.days ?? 30}` }],
    }),

    // System-wide daily snapshots (niv_system_daily) — progress-by-day charts
    getSystemDailySeries: builder.query({
      async queryFn({ days = 30 } = {}) {
        try {
          const series = await getSystemDailySeries({ days });
          return { data: serializeForRedux(series) };
        } catch (error) {
          console.error("[CK-7964] Error fetching system daily series:", {
            errorCode: "CK-7964",
            errorMessage: error.message,
            days,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: "NivTelemetry", id: "SYSTEM_DAILY" }],
    }),

    getClinicUsage: builder.query({
      async queryFn() {
        try {
          const usage = await getClinicUsageList();
          return { data: serializeForRedux(usage) };
        } catch (error) {
          console.error("[CK-7911] Error fetching clinic usage list:", {
            errorCode: "CK-7911",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: "NivUsage", id: "LIST" }],
    }),

    getClinicTelemetryDetail: builder.query({
      async queryFn({ clinicId, days = 90 }) {
        try {
          const detail = await getClinicTelemetryDetail(clinicId, { days });
          return { data: serializeForRedux(detail) };
        } catch (error) {
          console.error("[CK-7912] Error fetching clinic telemetry detail:", {
            errorCode: "CK-7912",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { clinicId }) => [
        { type: "NivTelemetry", id: clinicId },
      ],
    }),

    // Feature-tips catalog for a clinic + per-tip visibility toggled by NiV
    getClinicFeatureTips: builder.query({
      async queryFn(clinicId) {
        try {
          const tipsDoc = await getClinicFeatureTips(clinicId);
          return { data: serializeForRedux(tipsDoc) };
        } catch (error) {
          console.error("[CK-7913] Error fetching clinic feature tips:", {
            errorCode: "CK-7913",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, clinicId) => [
        { type: "NivFeatureTips", id: clinicId },
      ],
    }),

    setClinicFeatureTipVisibility: builder.mutation({
      async queryFn({ clinicId, featureKey, visible }) {
        try {
          await setClinicFeatureTipVisibility(clinicId, featureKey, visible);
          return { data: { success: true, featureKey, visible } };
        } catch (error) {
          console.error("[CK-7914] Error setting feature tip visibility:", {
            errorCode: "CK-7914",
            errorMessage: error.message,
            clinicId,
            featureKey,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { clinicId }) => [
        { type: "NivFeatureTips", id: clinicId },
      ],
    }),

    // On-demand recompute (one clinic or all) via the recomputeTelemetryScores
    // Cloud Function — refreshes the usage doc, feature status and feature
    // tips. The callable keeps its old name: renaming a deployed Gen2 function
    // orphans the old one.
    recomputeTelemetryScores: builder.mutation({
      async queryFn({ clinicId, all = false } = {}) {
        try {
          const fn = httpsCallable(functions, "recomputeTelemetryScores");
          const result = await fn(all ? { all: true } : { clinicId });
          return { data: result.data };
        } catch (error) {
          console.error("[CK-7918] Error recomputing clinic usage:", {
            errorCode: "CK-7918",
            errorMessage: error.message,
            clinicId: clinicId || null,
            all,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { clinicId, all } = {}) =>
        all
          ? ["NivTelemetry", "NivUsage", "NivFeatureTips"]
          : [
              { type: "NivTelemetry", id: "OVERVIEW" },
              { type: "NivTelemetry", id: clinicId },
              { type: "NivUsage", id: "LIST" },
              { type: "NivFeatureTips", id: clinicId },
            ],
    }),

    // ============================================
    // ERROR LOGS (niv_error_logs, written by the client error interceptor)
    // ============================================

    getErrorLogs: builder.query({
      async queryFn({ days = 7 } = {}) {
        try {
          const logs = await getErrorLogs({ days });
          return { data: serializeForRedux(logs) };
        } catch (error) {
          console.error("[CK-7919] Error fetching error logs:", {
            errorCode: "CK-7919",
            errorMessage: error.message,
            days,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: "NivErrorLogs", id: "LIST" }],
    }),

    updateErrorLogStatus: builder.mutation({
      async queryFn({ errorId, status }) {
        try {
          await updateErrorLogStatus(errorId, status);
          return { data: { errorId, status } };
        } catch (error) {
          console.error("[CK-7929] Error updating error log status:", {
            errorCode: "CK-7929",
            errorMessage: error.message,
            errorId,
            status,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      // Optimistic: patch every cached window instead of refetching 500 docs
      async onQueryStarted({ errorId, status }, { dispatch, queryFulfilled, getState }) {
        const patches = nivApi.util
          .selectInvalidatedBy(getState(), [{ type: "NivErrorLogs", id: "LIST" }])
          .filter((e) => e.endpointName === "getErrorLogs")
          .map((e) =>
            dispatch(
              nivApi.util.updateQueryData("getErrorLogs", e.originalArgs, (draft) => {
                const row = draft.find((r) => r.id === errorId);
                if (row) {
                  row.status = status;
                  row.handledAt = status === "handled" ? new Date().toISOString() : null;
                }
              })
            )
          );
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p.undo());
        }
      },
    }),

    deleteErrorLog: builder.mutation({
      async queryFn({ errorId }) {
        try {
          await deleteErrorLog(errorId);
          return { data: { errorId } };
        } catch (error) {
          console.error("[CK-7931] Error deleting error log:", {
            errorCode: "CK-7931",
            errorMessage: error.message,
            errorId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      async onQueryStarted({ errorId }, { dispatch, queryFulfilled, getState }) {
        const patches = nivApi.util
          .selectInvalidatedBy(getState(), [{ type: "NivErrorLogs", id: "LIST" }])
          .filter((e) => e.endpointName === "getErrorLogs")
          .map((e) =>
            dispatch(
              nivApi.util.updateQueryData("getErrorLogs", e.originalArgs, (draft) => {
                const idx = draft.findIndex((r) => r.id === errorId);
                if (idx >= 0) draft.splice(idx, 1);
              })
            )
          );
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p.undo());
        }
      },
    }),

    deleteAllErrorLogs: builder.mutation({
      async queryFn() {
        try {
          const result = await deleteAllErrorLogs();
          return { data: result };
        } catch (error) {
          console.error("[CK-7932] Error deleting all error logs:", {
            errorCode: "CK-7932",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "NivErrorLogs", id: "LIST" }],
    }),

    // ============================================
    // WHATSAPP (Meta Cloud API) — nivWhatsAppAdmin callable
    // Two sender channels ("users" / "patients"); see functions/whatsappHelper.js
    // ============================================

    // Config + numbers + Meta template approval state
    // ============================================================
    // GROW PAYMENTS — NiV control plane (grow_integration.md Phase 8)
    // All reads/writes go through the `nivGrowAdmin` callable (nivAdmin claim).
    // CK-7430..7434
    // ============================================================
    getNivGrowClinicState: builder.query({
      async queryFn({ clinicId }) {
        try {
          const fn = httpsCallable(functions, "nivGrowAdmin");
          const res = await fn({ action: "clinicState", payload: { clinicId } });
          return { data: res.data };
        } catch (error) {
          console.error("[CK-7430] nivApi.getNivGrowClinicState failed", { errorCode: "CK-7430", errorMessage: error.message, clinicId, timestamp: new Date().toISOString() });
          return { error: { message: error.message } };
        }
      },
      providesTags: (r, e, { clinicId }) => [{ type: "NivGrow", id: `STATE_${clinicId}` }],
    }),
    getNivGrowLinks: builder.query({
      async queryFn({ clinicId, status, limit }) {
        try {
          const fn = httpsCallable(functions, "nivGrowAdmin");
          const res = await fn({ action: "listLinks", payload: { clinicId, status, limit } });
          return { data: res.data?.rows || [] };
        } catch (error) {
          console.error("[CK-7431] nivApi.getNivGrowLinks failed", { errorCode: "CK-7431", errorMessage: error.message, clinicId, timestamp: new Date().toISOString() });
          return { error: { message: error.message } };
        }
      },
      providesTags: (r, e, { clinicId }) => [{ type: "NivGrow", id: `LINKS_${clinicId}` }],
    }),
    getNivGrowInbox: builder.query({
      async queryFn({ kind, clinicId, outcome, limit }) {
        try {
          const fn = httpsCallable(functions, "nivGrowAdmin");
          const res = await fn({ action: "listInbox", payload: { kind, clinicId, outcome, limit } });
          return { data: res.data?.rows || [] };
        } catch (error) {
          console.error("[CK-7432] nivApi.getNivGrowInbox failed", { errorCode: "CK-7432", errorMessage: error.message, timestamp: new Date().toISOString() });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: "NivGrow", id: "INBOX" }],
    }),
    getNivGrowUnmatched: builder.query({
      async queryFn({ clinicId, resolved, limit }) {
        try {
          const fn = httpsCallable(functions, "nivGrowAdmin");
          const res = await fn({ action: "listUnmatched", payload: { clinicId, resolved, limit } });
          return { data: res.data?.rows || [] };
        } catch (error) {
          console.error("[CK-7433] nivApi.getNivGrowUnmatched failed", { errorCode: "CK-7433", errorMessage: error.message, timestamp: new Date().toISOString() });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: "NivGrow", id: "UNMATCHED" }],
    }),
    runNivGrowAction: builder.mutation({
      async queryFn({ action, payload }) {
        try {
          const fn = httpsCallable(functions, "nivGrowAdmin");
          const res = await fn({ action, payload });
          return { data: res.data };
        } catch (error) {
          console.error("[CK-7434] nivApi.runNivGrowAction failed", { errorCode: "CK-7434", errorMessage: error.message, action, timestamp: new Date().toISOString() });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (r, e, { payload }) => [
        { type: "NivGrow", id: "INBOX" },
        { type: "NivGrow", id: "UNMATCHED" },
        ...(payload?.clinicId ? [{ type: "NivGrow", id: `STATE_${payload.clinicId}` }, { type: "NivGrow", id: `LINKS_${payload.clinicId}` }, { type: "NivClinic", id: payload.clinicId }] : []),
      ],
    }),

    getWhatsAppStatus: builder.query({
      async queryFn() {
        try {
          const fn = httpsCallable(functions, "nivWhatsAppAdmin");
          const res = await fn({ action: "status" });
          return { data: res.data };
        } catch (error) {
          console.error("[CK-7977] Error in getWhatsAppStatus query:", {
            errorCode: "CK-7977",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: "NivWhatsApp", id: "STATUS" }],
    }),

    // Last N audit rows (outbound + inbound), optional channel filter
    getWhatsAppRecentMessages: builder.query({
      async queryFn({ channel, limit } = {}) {
        try {
          const fn = httpsCallable(functions, "nivWhatsAppAdmin");
          const res = await fn({ action: "recentMessages", payload: { channel, limit } });
          return { data: res.data?.messages || [] };
        } catch (error) {
          console.error("[CK-7978] Error in getWhatsAppRecentMessages query:", {
            errorCode: "CK-7978",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: "NivWhatsApp", id: "MESSAGES" }],
    }),

    // syncTemplates | sendTest | resendLead
    runWhatsAppAction: builder.mutation({
      async queryFn({ action, payload }) {
        try {
          const fn = httpsCallable(functions, "nivWhatsAppAdmin");
          const res = await fn({ action, payload });
          return { data: res.data };
        } catch (error) {
          console.error("[CK-7979] Error in runWhatsAppAction mutation:", {
            errorCode: "CK-7979",
            errorMessage: error.message,
            action,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { action, payload }) => {
        const tags = [
          { type: "NivWhatsApp", id: "STATUS" },
          { type: "NivWhatsApp", id: "MESSAGES" },
        ];
        if (action === "resendLead" && payload?.requestId) {
          tags.push({ type: "NivCallRequest", id: payload.requestId });
          tags.push({ type: "NivCallRequest", id: "LIST" });
        }
        return tags;
      },
    }),

    // ============================================================
    // Messaging router control plane (whatsapp_integration.md Phase 1)
    // nivMessagingAdmin callable — NiV "הודעות" tab. CK-7400–7408.
    // ============================================================

    // Policy doc + registry + health + bad templates
    getMessagingPolicy: builder.query({
      async queryFn() {
        try {
          const fn = httpsCallable(functions, "nivMessagingAdmin");
          const res = await fn({ action: "getPolicy" });
          return { data: res.data };
        } catch (error) {
          console.error("[CK-7400] Error in getMessagingPolicy query:", {
            errorCode: "CK-7400",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: "NivMessaging", id: "POLICY" }],
    }),

    // Validated patch: { whatsappEnabled?, smsEnabled?, fallbackEnabled?, channels?, types?: { [id]: { enabled?, channels? } | null } }
    setMessagingPolicy: builder.mutation({
      async queryFn({ patch }) {
        try {
          const fn = httpsCallable(functions, "nivMessagingAdmin");
          const res = await fn({ action: "setPolicy", payload: { patch } });
          return { data: res.data };
        } catch (error) {
          console.error("[CK-7401] Error in setMessagingPolicy mutation:", {
            errorCode: "CK-7401",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "NivMessaging", id: "POLICY" }],
    }),

    // Seeds niv_settings/messaging with the default policy when missing
    initMessagingPolicy: builder.mutation({
      async queryFn() {
        try {
          const fn = httpsCallable(functions, "nivMessagingAdmin");
          const res = await fn({ action: "initPolicy" });
          return { data: res.data };
        } catch (error) {
          console.error("[CK-7402] Error in initMessagingPolicy mutation:", {
            errorCode: "CK-7402",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "NivMessaging", id: "POLICY" }],
    }),

    // Inbound replies (niv_inbox) — { items, unreadCount }
    getNivInbox: builder.query({
      async queryFn({ unreadOnly, limit } = {}) {
        try {
          const fn = httpsCallable(functions, "nivMessagingAdmin");
          const res = await fn({ action: "inbox", payload: { unreadOnly, limit } });
          return { data: res.data };
        } catch (error) {
          console.error("[CK-7403] Error in getNivInbox query:", {
            errorCode: "CK-7403",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: "NivMessaging", id: "INBOX" }],
    }),

    // Full WhatsApp conversation with one contact — { waId, messages, conversation }
    getNivInboxThread: builder.query({
      async queryFn({ waId, limit } = {}) {
        try {
          const fn = httpsCallable(functions, "nivMessagingAdmin");
          const res = await fn({ action: "thread", payload: { waId, limit } });
          return { data: res.data };
        } catch (error) {
          console.error("[CK-7411] Error in getNivInboxThread query:", {
            errorCode: "CK-7411",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, arg) => [{ type: "NivMessaging", id: `THREAD_${arg?.waId || ""}` }],
    }),

    markInboxRead: builder.mutation({
      async queryFn({ inboxId, read = true }) {
        try {
          const fn = httpsCallable(functions, "nivMessagingAdmin");
          const res = await fn({ action: "markRead", payload: { inboxId, read } });
          return { data: res.data };
        } catch (error) {
          console.error("[CK-7404] Error in markInboxRead mutation:", {
            errorCode: "CK-7404",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      // Optimistic: flip readAt locally so the row moves without a refetch flash
      async onQueryStarted({ inboxId, read = true }, { dispatch, queryFulfilled, getState }) {
        const patches = nivApi.util
          .selectInvalidatedBy(getState(), [{ type: "NivMessaging", id: "INBOX" }])
          .filter((e) => e.endpointName === "getNivInbox")
          .map((e) =>
            dispatch(
              nivApi.util.updateQueryData("getNivInbox", e.originalArgs, (draft) => {
                const row = draft?.items?.find((r) => r.id === inboxId);
                if (!row) return;
                const wasUnread = !row.readAt;
                row.readAt = read ? new Date().toISOString() : null;
                if (draft.unreadCount != null) {
                  if (read && wasUnread) draft.unreadCount = Math.max(0, draft.unreadCount - 1);
                  if (!read && !wasUnread) draft.unreadCount += 1;
                }
              })
            )
          );
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p.undo());
        }
      },
      invalidatesTags: [{ type: "NivMessaging", id: "INBOX" }],
    }),

    setInboxIdentity: builder.mutation({
      async queryFn({ inboxId, identity }) {
        try {
          const fn = httpsCallable(functions, "nivMessagingAdmin");
          const res = await fn({ action: "setIdentity", payload: { inboxId, identity } });
          return { data: res.data };
        } catch (error) {
          console.error("[CK-7405] Error in setInboxIdentity mutation:", {
            errorCode: "CK-7405",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "NivMessaging", id: "INBOX" }],
    }),

    // Outbox: message_log ∪ un-routed whatsapp_messages — { rows }
    getMessagingOutbox: builder.query({
      async queryFn(filters = {}) {
        try {
          const fn = httpsCallable(functions, "nivMessagingAdmin");
          const res = await fn({ action: "outbox", payload: filters });
          return { data: res.data?.rows || [] };
        } catch (error) {
          console.error("[CK-7406] Error in getMessagingOutbox query:", {
            errorCode: "CK-7406",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: "NivMessaging", id: "OUTBOX" }],
    }),

    // Re-invokes the router with the stored inputs of a message_log row
    resendMessage: builder.mutation({
      async queryFn({ logId }) {
        try {
          const fn = httpsCallable(functions, "nivMessagingAdmin");
          const res = await fn({ action: "resend", payload: { logId } });
          return { data: res.data };
        } catch (error) {
          console.error("[CK-7407] Error in resendMessage mutation:", {
            errorCode: "CK-7407",
            errorMessage: error.message,
            logId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [
        { type: "NivMessaging", id: "OUTBOX" },
        { type: "NivMessaging", id: "USAGE" },
        { type: "NivWhatsApp", id: "MESSAGES" },
      ],
    }),

    // Daily series + totals + fallback rate + top clinics + health (also feeds briefing chips)
    getMessagingUsage: builder.query({
      async queryFn({ days } = {}) {
        try {
          const fn = httpsCallable(functions, "nivMessagingAdmin");
          const res = await fn({ action: "usage", payload: { days } });
          return { data: res.data };
        } catch (error) {
          console.error("[CK-7408] Error in getMessagingUsage query:", {
            errorCode: "CK-7408",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: "NivMessaging", id: "USAGE" }],
    }),

    // Phase 4.7 — manual "שלח שוב" for an approved-but-unregistered prospect (router, prospect_reg_nudge)
    sendProspectNudge: builder.mutation({
      async queryFn({ phone, fullName }) {
        try {
          const fn = httpsCallable(functions, "nivMessagingAdmin");
          const res = await fn({ action: "prospectNudge", payload: { phone, fullName } });
          return { data: res.data?.result || null };
        } catch (error) {
          console.error("[CK-7410] Error in sendProspectNudge mutation:", {
            errorCode: "CK-7410",
            errorMessage: error.message,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: ["NivProspect", { type: "NivMessaging", id: "OUTBOX" }],
    }),

    // Clinic detail v2 "הודעות" section — { counts, rows }
    getClinicMessaging: builder.query({
      async queryFn({ clinicId, days, limit } = {}) {
        try {
          const fn = httpsCallable(functions, "nivMessagingAdmin");
          const res = await fn({ action: "clinicMessaging", payload: { clinicId, days, limit } });
          return { data: res.data };
        } catch (error) {
          console.error("[CK-7409] Error in getClinicMessaging query:", {
            errorCode: "CK-7409",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { clinicId }) => [{ type: "NivMessaging", id: `CLINIC_${clinicId}` }],
    }),
  }),
});

// Export hooks
export const {
  // WhatsApp
  useGetWhatsAppStatusQuery,
  useGetWhatsAppRecentMessagesQuery,
  useRunWhatsAppActionMutation,
  useGetNivGrowClinicStateQuery,
  useGetNivGrowLinksQuery,
  useGetNivGrowInboxQuery,
  useGetNivGrowUnmatchedQuery,
  useRunNivGrowActionMutation,
  // Messaging router control plane
  useGetMessagingPolicyQuery,
  useSetMessagingPolicyMutation,
  useInitMessagingPolicyMutation,
  useGetNivInboxQuery,
  useGetNivInboxThreadQuery,
  useMarkInboxReadMutation,
  useSetInboxIdentityMutation,
  useGetMessagingOutboxQuery,
  useResendMessageMutation,
  useGetMessagingUsageQuery,
  useGetClinicMessagingQuery,
  useSendProspectNudgeMutation,
  // Stats & Alerts
  useGetNivDashboardStatsQuery,
  useGetNivAlertsQuery,
  // NEW: Source-of-truth clinic fetching
  useGetNivClinicsFromSourceQuery,
  useGetNivClinicFullDetailQuery,
  useGetNivClinicActivitySeriesQuery,
  useUpdateClinicFieldMutation,
  useEnsureCrmRecordMutation,
  // Legacy Clinic Management
  useGetNivClinicsQuery,
  useGetNivClinicDetailQuery,
  useGetNivClinicUsageStatsQuery,
  useGetAllClinicsBasicQuery,
  useCreateNivClinicManagementMutation,
  useUpdateNivClinicManagementMutation,
  useUpdateNivClinicStatusMutation,
  useAddNivCallLogMutation,
  useAddNivPaymentRecordMutation,
  useUpdateClinicSmsLimitMutation,
  // Tasks
  useGetNivTasksQuery,
  useGetNivTaskQuery,
  useCreateNivTaskMutation,
  useUpdateNivTaskMutation,
  useDeleteNivTaskMutation,
  useGetNivTasksConfigQuery,
  useUpdateNivTasksConfigMutation,
  useBulkUpdateNivTasksMutation,
  // Onboarding
  useGetOnboardingRequestsQuery,
  useMarkOnboardingCompletedMutation,
  // Churn Resolutions
  useGetChurnResolutionsQuery,
  useResolveChurnAlertMutation,
  useGetMonitoringAlertsQuery,
  useSnoozeMonitoringAlertMutation,
  useSnoozeMonitoringAlertsBulkMutation,
  // Lenny (AI support)
  useGetLennyReviewsQuery,
  useSetLennyReviewStatusMutation,
  useGetClinicLennyHistoryQuery,
  useGetTherapistAdditionsQuery,
  useResolveTherapistAdditionMutation,
  // Call Requests
  useGetCallRequestsQuery,
  useCreateCallRequestMutation,
  useCreateLeadFromLandingPageMutation,
  useUpdateCallRequestStatusMutation,
  useDeleteCallRequestMutation,
  useAddCallRequestNoteMutation,
  // Plans (System Plans - hard-coded)
  useGetNivPlansQuery,
  useGetSystemPlansQuery,
  // Legacy plan mutations (deprecated)
  useCreateNivPlanMutation,
  useUpdateNivPlanMutation,
  useDeleteNivPlanMutation,
  useSeedDefaultPlansMutation,
  // New plan mutations
  useUpdateClinicPlanDirectlyMutation,
  useUpdateClinicCustomPaymentMutation,
  useSetClinicPlanEndDateMutation,
  // Sync & Revenue
  useSyncClinicsToManagementMutation,
  useUpdateClinicPlanMutation,
  useGetTotalMonthlyRevenueQuery,
  // System Notifications
  useGetSystemNotificationsQuery,
  useDeleteSystemNotificationMutation,
  useGetFoodPromotionRequestsQuery,
  useApproveFoodPromotionRequestMutation,
  useRejectFoodPromotionRequestMutation,
  useSeedFoodCatalogMutation,
  useDeleteClinicHardMutation,
  // System Messages (broadcasts)
  useGetNivSystemMessagesQuery,
  useCreateNivSystemMessageMutation,
  useDeleteNivSystemMessageMutation,
  // Registration Whitelist
  useGetRegistrationWhitelistQuery,
  useAddPhoneToWhitelistMutation,
  useRemovePhoneFromWhitelistMutation,
  // Clinic CRM (manual admin layer)
  useGetNivClinicsOverviewQuery,
  useUpdateNivClinicCrmMutation,
  useAddNivCrmPaymentMutation,
  useAddNivCrmPaymentAndExtendMutation,
  useDeleteNivCrmPaymentMutation,
  useAddNivCrmNoteMutation,
  // Clinic tags + contact tracking + activation (clinics tab)
  useGetNivClinicTagsRegistryQuery,
  useSaveNivClinicTagMutation,
  useDeleteNivClinicTagMutation,
  useSetNivClinicTagIdsMutation,
  useAddNivCrmContactMutation,
  useDeleteNivCrmContactMutation,
  useDismissNivContactNeedMutation,
  useGetClinicsActivationQuery,
  // Prospects (unified CRM pipeline)
  useGetProspectsBoardQuery,
  useCreateProspectMutation,
  useUpdateProspectDetailsMutation,
  useDismissStaleApprovedProspectMutation,
  useUpdateProspectStageMutation,
  useAddProspectNoteMutation,
  useApproveProspectRegistrationMutation,
  useRevokeProspectRegistrationMutation,
  useDeleteProspectMutation,
  useLinkProspectToClinicMutation,
  useUnlinkProspectFromClinicMutation,
  useUpdateProspectFreeNotesMutation,
  useUpdateNivCrmFreeNotesMutation,
  useUpdateProspectNextActionMutation,
  useUpdateNivCrmNextActionMutation,
  useSetStandingOrderMonthStatusMutation,
  useGenerateNivChatSummaryMutation,
  useNivPrintListPdfMutation,
  // Telemetry & scores
  useGetTelemetryOverviewQuery,
  useGetSystemDailySeriesQuery,
  useGetClinicUsageQuery,
  useGetPublicAnalyticsQuery,
  useGetClinicTelemetryDetailQuery,
  useGetClinicFeatureTipsQuery,
  useSetClinicFeatureTipVisibilityMutation,
  useRecomputeTelemetryScoresMutation,
  // Error logs
  useGetErrorLogsQuery,
  useUpdateErrorLogStatusMutation,
  useDeleteErrorLogMutation,
  useDeleteAllErrorLogsMutation,
} = nivApi;
